import { useEffect, useMemo, useRef, useState } from 'react';
import { publishInstagram, publishWhatsapp } from '@/lib/api';
import { resumenJornadaWA, type FilaResumen } from '@/lib/waLive';
import { TelegramSerieCard, type TgFila } from '@/components/TelegramSerieCard';

/* Modal "Resumen de jornada" (fuente WA, modo admin): tarjetas con la
 * actuación de TODOS los españoles del día y publicación SOLO a INSTAGRAM
 * vía publish_instagram (webhook de Make). Las tarjetas van PAGINADAS en
 * lienzos fijos 1080×1350 (4:5, el máximo de IG) — todas con la misma
 * proporción porque el carrusel de IG recorta al ratio de la primera.
 * 1 página → post de una imagen; varias → carrusel (router en Make). */

const IG_W = 1080;
const IG_H = 1350;
const FILAS_POR_PAGINA = 6;

interface Props {
  resKey: string;
  onClose: () => void;
}

function filaDe(f: FilaResumen): TgFila {
  return {
    puesto: f.puesto, nombre: f.nombre, club: f.club, marca: f.marca, pais: f.pais,
    medalla: f.medalla,
    chips: f.estado ? [{ t: f.estado, hi: f.clasificado, rojo: f.eliminado }] : undefined,
  };
}

export function ResumenJornadaModal({ resKey, onClose }: Props) {
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof resumenJornadaWA>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const [publicandoWa, setPublicandoWa] = useState(false);
  const [publicadoWa, setPublicadoWa] = useState(false);
  // '' = jornada completa · manana/tarde = solo esa sesión de Eugene
  const [parte, setParte] = useState<'' | 'manana' | 'tarde'>('');
  const pagRefs = useRef<(HTMLDivElement | null)[]>([]);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    let cancel = false;
    setDatos(null);
    resumenJornadaWA(resKey, parte)
      .then((d) => { if (!cancel) setDatos(d); })
      .catch((e) => { if (!cancel) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancel = true; };
  }, [resKey, parte]);

  const paginas = useMemo(() => {
    if (!datos) return [];
    const filas = datos.filas.map(filaDe);
    const out: TgFila[][] = [];
    for (let i = 0; i < filas.length; i += FILAS_POR_PAGINA) out.push(filas.slice(i, i + FILAS_POR_PAGINA));
    return out;
  }, [datos]);

  useEffect(() => {
    if (previewRef.current) setScale(previewRef.current.clientWidth / IG_W);
  }, [datos]);

  const cardProps = (filas: TgFila[]) => ({
    competicion: datos!.competicion,
    titulo: datos!.titulo,
    subtitulo: datos!.subtitulo
      + (parte ? ` · Sesión de ${parte === 'manana' ? 'mañana' : 'tarde'}` : '')
      + (paginas.length > 1 ? ` · ${paginas.indexOf(filas) + 1}/${paginas.length}` : ''),
    filas,
    medallas: true,
    nota: null,
  });

  async function publicarWhatsApp() {
    if (!datos || !paginas.length) return;
    setPublicandoWa(true);
    setError(null);
    try {
      const html2canvas = (await import('html2canvas')).default;
      for (let i = 0; i < pagRefs.current.length; i++) {
        const el = pagRefs.current[i];
        if (!el) continue;
        const canvas = await html2canvas(el, { width: IG_W, height: IG_H, scale: 2, backgroundColor: null, useCORS: true });
        await publishWhatsapp({
          photo: canvas.toDataURL('image/png'),
          caption: i === 0
            ? `🇪🇸 ${datos.titulo} · ${datos.subtitulo}${parte ? ` · sesión de ${parte === 'manana' ? 'mañana' : 'tarde'}` : ''}`
            : undefined,
        });
      }
      setPublicadoWa(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublicandoWa(false);
    }
  }

  async function publicar() {
    if (!datos || !paginas.length) return;
    setPublicando(true);
    setError(null);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const photos: string[] = [];
      for (const el of pagRefs.current) {
        if (!el) continue;
        const canvas = await html2canvas(el, { width: IG_W, height: IG_H, scale: 2, backgroundColor: null, useCORS: true });
        photos.push(canvas.toDataURL('image/png'));
      }
      const caption = [
        `🇪🇸 ${datos.titulo} — ${datos.subtitulo}`,
        datos.competicion, '',
        ...datos.filas.map((f) => {
          const estado = f.medalla ? ({ '1': '🥇 ORO', '2': '🥈 PLATA', '3': '🥉 BRONCE' } as Record<string, string>)[f.medalla] : f.estado;
          return `${f.nombre} · ${f.club} — ${f.marca || '—'} · ${estado}`;
        }),
      ].join('\n');
      await publishInstagram({ photos, caption });
      setPublicado(true);
      window.setTimeout(onClose, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublicando(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ld-card" style={{ width: '100%', maxWidth: 720, marginTop: 24 }}>
        <div className="ld-spread" style={{ marginBottom: 10, gap: 10 }}>
          <h2 className="ld-card__title" style={{ margin: 0 }}>Publicar en Instagram</h2>
          <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={onClose}>Cerrar</button>
        </div>
        {!datos && !error ? (
          <p className="muted" style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
            <span className="ld-spinner" aria-hidden /> Preparando el resumen…
          </p>
        ) : null}
        {datos && paginas.length ? (
          <>
            <div ref={previewRef} className="ld-stack" style={{ gap: 12 }}>
              {paginas.map((filas, i) => (
                <div key={i} style={{ width: '100%', height: IG_H * scale, overflow: 'hidden', borderRadius: 10, border: '1px solid var(--ld-line)' }}>
                  <div style={{ width: IG_W, height: IG_H, transform: `scale(${scale})`, transformOrigin: 'top left', background: '#f0dcc1' }}>
                    <TelegramSerieCard {...cardProps(filas)} />
                  </div>
                </div>
              ))}
            </div>
            {/* Gemelos 1080×1350 a tamaño real, fuera de pantalla (html2canvas). */}
            <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
              {paginas.map((filas, i) => (
                <div key={i} ref={(el) => { pagRefs.current[i] = el; }} style={{ width: IG_W, height: IG_H, overflow: 'hidden', background: '#f0dcc1' }}>
                  <TelegramSerieCard {...cardProps(filas)} />
                </div>
              ))}
            </div>
            <div className="ld-row" style={{ marginTop: 14, gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {(['', 'manana', 'tarde'] as const).map((pt) => (
                <button
                  key={pt || 'todo'}
                  type="button"
                  className={`ld-btn ld-btn--small ${parte === pt ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
                  onClick={() => setParte(pt)}
                >
                  {pt === '' ? 'Jornada completa' : pt === 'manana' ? 'Sesión mañana' : 'Sesión tarde'}
                </button>
              ))}
            </div>
            <div className="ld-row" style={{ marginTop: 10, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="ld-btn ld-btn--ghost"
                disabled={publicandoWa || publicadoWa}
                onClick={() => void publicarWhatsApp()}
              >
                {publicadoWa ? 'WhatsApp ✓' : publicandoWa ? 'Publicando…' : 'Publicar en WhatsApp'}
              </button>
              <button
                type="button"
                className="ld-btn ld-btn--primary"
                disabled={publicando || publicado}
                onClick={() => void publicar()}
              >
                {publicado ? 'Publicado ✓' : publicando ? 'Publicando…' : `Publicar en Instagram (${paginas.length === 1 ? '1 imagen' : `carrusel de ${paginas.length}`})`}
              </button>
              <span className="muted mono" style={{ fontSize: 11 }}>
                Lienzos 1080×1350 (4:5) vía Make; 1 página = post normal, varias = carrusel.
              </span>
            </div>
          </>
        ) : datos && !paginas.length ? (
          <p className="ld-card__hint">Sin actuaciones de españoles en esta jornada.</p>
        ) : null}
        {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
      </div>
    </div>
  );
}
